import {
  Badge,
  Box,
  BoxProps,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import {useState} from "react";
import {Close} from "@mui/icons-material";
import Grid from "@mui/material/Grid2";
import {ArmDocument} from "../../../equipmentdb/types/pj1dbApiArm";
import {ArmListItem} from "../lists/ArmListItem";
import Pjdb1Button from "../../../../components/inputs/Pjdb1Button";
import WithLabel from "./WithLabel";
import {FormArmSearchCond} from "../../../equipmentdb/types/armForm";
import {handleInputChange} from "../../../../utils/formUtil";
import {FetchMessage, Status, STATUSES} from "../../../../hooks/useFetchAllQuery";

export interface ArmSelectionFieldProps extends SearchFormProps {
  arms: ArmDocument[];
  selectedArm: ArmDocument | null;
  onChange: (arm: ArmDocument | null) => void;
}

export function ArmSelectionField(props: ArmSelectionFieldProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <WithLabel label='アーム選択'>
        <Stack gap={1} mt={1}>
          <Pjdb1Button
            label='アームを選択する'
            variant='outlined'
            color='inherit'
            onClick={() => setOpen(true)}
            sx={{width: 'fit-content'}}
          />
          {props.selectedArm && (
            <Box>
              <Badge
                badgeContent={
                  <Close
                    fontSize='medium'
                    onClick={() => props.onChange(null)}
                    sx={{color: 'white', bgcolor: 'gray', borderRadius: 4}
                    }/>
                }>
                <ArmListItem arm={props.selectedArm}/>
              </Badge>
            </Box>
          )}
        </Stack>
      </WithLabel>
      <ArmSelectionDialog
        /* eslint-disable-next-line react/jsx-props-no-spreading */
        {...props}
        open={open}
        onClose={() => setOpen(false)}
        onClick={(arm) => {
          props.onChange(arm);
        }}
      />
    </>
  )
}

function ArmSelectionDialog(props: SearchFormProps & {
  arms: ArmDocument[];
  selectedArm: ArmDocument | null;
  open: boolean;
  onClose: () => void;
  onClick: (arm: ArmDocument) => void;
}) {
  const selectedStyle: BoxProps['sx'] = {
    outlineColor: (theme) => theme.palette.primary.light,
    outlineStyle: "solid",
    outlineWidth: 6,
    borderRadius: 1,
  };

  const sortList = (armListData:ArmDocument[],sortOrder:string):ArmDocument[] => {
    let list = armListData;
    if(sortOrder==="name"){
      list = list.sort((a,b) => a.attr.armInfo.name.localeCompare(b.attr.armInfo.name));
    }else if(sortOrder==="manufacturer"){
      list = list.sort((a,b) => a.attr.armInfo.manufacturer.localeCompare(b.attr.armInfo.manufacturer));
    }else if(sortOrder==="latest"){
      list = list.sort((a,b) => b.attr.registrationDate - a.attr.registrationDate );
    }
    return list;
  }

  return (
    <Dialog disableEscapeKeyDown open={props.open} onClose={props.onClose} fullWidth maxWidth='xl'>
      <DialogTitle>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          接続アームを選択
          <Box onClick={props.onClose} p={1} display='flex' alignItems='center'>
            <Close/>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack gap={4}>
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <SearchForm {...props} />

          <Box display='flex' flexWrap='nowrap' justifyContent='space-between'>
            <Typography variant="subtitle1" sx={{fontWeight: "bold"}}>
              検索結果一覧
            </Typography>
            <Box display='flex' gap={2}>
              <Box>
                {props.searchCond.listCount > 0 ? `${props.searchCond.listCount.toLocaleString()}件` : ""}
              </Box>
              <FormControl sx={{minWidth: 120}} size="small">
                <InputLabel id="sort-order-label">並び順</InputLabel>
                <Select
                  labelId="sort-order-label"
                  name="sortOrder"
                  value={props.searchCond.sortOrder}
                  label="並び順"
                  onChange={(e) => {
                    props.onChangeSearchCond({
                      ...props.searchCond,
                      sortOrder: e.target.value
                    });
                  }}
                  sx={{bgcolor: "white"}}
                >
                  <MenuItem value="name">製品名</MenuItem>
                  <MenuItem value="manufacturer">製造者</MenuItem>
                  <MenuItem value="latest">最新</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Box display='flex' flexWrap='wrap' gap={2}>
            {sortList(props.arms, props.searchCond.sortOrder).map((arm) => (
              <ArmListItem
                key={arm.attr.armId}
                arm={arm}
                onClick={() => props.onClick(arm)}
                sx={{
                  outlineColor: 'inherit',
                  outlineStyle: "solid",
                  outlineWidth: 1,
                  borderRadius: 1,
                  ...(arm.attr.armId === props.selectedArm?.attr.armId ? selectedStyle : {})
                }}
              />
            ))}
          </Box>

          <FetchMessage data={props.arms} status={props.searchStatus}/>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Pjdb1Button label="閉じる" color='inherit' variant='contained' onClick={props.onClose}/>
      </DialogActions>
    </Dialog>
  )
}

interface SearchFormProps {
  searchStatus: Status
  searchCond: FormArmSearchCond
  onChangeSearchCond: (searchCond: FormArmSearchCond) => void
  onSubmit: React.FormEventHandler<HTMLFormElement> | undefined
}

function SearchForm(props: SearchFormProps) {
  const [searchCond, setSearchCond] = useState<SearchFormProps['searchCond']>(props.searchCond);

  return (
    <Card sx={{p: 3}}>
      <Typography variant="subtitle1" sx={{fontWeight: "bold"}}>検索条件</Typography>
      <Box sx={{ml: 2}}>
        <form onSubmit={props.onSubmit}>
          <InputLabel variant='standard' sx={{mt: 2, color: 'text.primary'}}>検索キーワード</InputLabel>

          <Grid container>
            <Grid size={{xs: 5}}>
              <TextField
                label="製品名、製造者、型番、特徴などを入力"
                name="keyword" value={props.searchCond.keyword}
                variant="outlined" size="small"
                margin="dense" fullWidth
                onChange={(e) => {
                  props.onChangeSearchCond({
                    ...searchCond,
                    keyword: e.target.value,
                  });
                  handleInputChange<SearchFormProps['searchCond']>(e, searchCond, setSearchCond)
                }}
              />
            </Grid>
          </Grid>
          <Box sx={{mt: 1}}>
            <Pjdb1Button
              label="検索"
              variant="contained"
              color="primary"
              isLoading={props.searchStatus === STATUSES.FETCHING}
              type='submit'
            />
          </Box>
        </form>
      </Box>
    </Card>
  )
}
